import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export function useWarmup() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const promptsQuery = useQuery({
    queryKey: ["warmup_prompts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("warmup_prompts")
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const responsesQuery = useQuery({
    queryKey: ["warmup_responses", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("warmup_responses")
        .select("*")
        .eq("user_id", user!.id);
      if (error) throw error;
      return data;
    },
  });

  const saveDraft = useMutation({
    mutationFn: async ({ promptId, answerText }: { promptId: string; answerText: string }) => {
      const existing = responsesQuery.data?.find((r) => r.prompt_id === promptId);
      if (existing) {
        const { error } = await supabase
          .from("warmup_responses")
          .update({ answer_text: answerText, status: "draft" })
          .eq("id", existing.id);
        if (error) throw error;
      } else {
        if (!user) throw new Error("Not authenticated");
        const { error } = await supabase
          .from("warmup_responses")
          .insert({ user_id: user.id, prompt_id: promptId, answer_text: answerText, status: "draft" });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["warmup_responses", user?.id] });
    },
  });

  const submit = useMutation({
    mutationFn: async ({ promptId, answerText }: { promptId: string; answerText: string }) => {
      const existing = responsesQuery.data?.find((r) => r.prompt_id === promptId);
      const now = new Date().toISOString();
      if (existing) {
        const { error } = await supabase
          .from("warmup_responses")
          .update({ answer_text: answerText, status: "submitted", submitted_at: now })
          .eq("id", existing.id);
        if (error) throw error;
      } else {
        if (!user) throw new Error("Not authenticated");
        const { error } = await supabase
          .from("warmup_responses")
          .insert({ user_id: user.id, prompt_id: promptId, answer_text: answerText, status: "submitted", submitted_at: now });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["warmup_responses", user?.id] });
    },
  });

  return {
    prompts: promptsQuery.data ?? [],
    responses: responsesQuery.data ?? [],
    isLoading: promptsQuery.isLoading || responsesQuery.isLoading,
    saveDraft,
    submit,
  };
}
